import { Box, Button, CircularProgress, FormHelperText, IconButton, TextField, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { Icons } from '../../../utils/utilities'
import { ACCESS_END_WARNING_THRESHOLD_SECONDS, MAX_EXTENSION_REASON_LENGTH } from '../../../shared/utils/constants'
import { useOnlineSessionsStore } from '../../../store/onlinesessions.store'

const TICK_INTERVAL_MS = 1000

const PHASE = { CLOSING: "closing", EXPIRED: "expired" }

const formatRemainingTime = ( seconds ) => {
    const safeSeconds = Math.max( 0, Math.floor( seconds ) )
    const minutes = Math.floor( safeSeconds / 60 )
    return `${String( minutes ).padStart( 2, "0" )}:${String( safeSeconds % 60 ).padStart( 2, "0" )}`
}

// Reads the <video> element rendered by VideoPlayer inside the dialog, so the tracker
// does not depend on VideoPlayer exposing play/pause callbacks.
const isVideoPlaying = ( containerEl ) => {
    const videoEl = containerEl?.querySelector?.( "video" )
    return Boolean( videoEl && !videoEl.paused && !videoEl.ended && videoEl.readyState > 2 )
}

/**
 * Warns the student when their access window for the video is about to end (or has ended)
 * while they are still watching. Progress watched after `access_ends_at` is not synced to the
 * backend, so without this the student could keep watching and never get attendance.
 *
 * Render it with `key={recordingDetails.id}` so dismissals reset per video.
 */
const AccessEndTimeTracker = ( {
    recordingDetails,
    containerEl,
    videoCompleted = false,
    warningThresholdInSeconds = ACCESS_END_WARNING_THRESHOLD_SECONDS,
    canRequestExtension = false,
    extensionRequest = null,
    requestingExtension = false,
    onRequestExtension,
} ) => {

    const [remainingTimeInSeconds, setRemainingTimeInSeconds] = useState( Infinity )
    const [hasStartedPlaying, setHasStartedPlaying] = useState( false )
    const [dismissedPhase, setDismissedPhase] = useState( null )
    const [reason, setReason] = useState( "" )

    const OnlineSessionsStore = useOnlineSessionsStore()
    const meeting = OnlineSessionsStore.getSessionMetadata

    const { palette } = useTheme()

    const classDay = dayjs( meeting.starts_at ).isSame( dayjs(), 'day' )
    const accessEndsAt = recordingDetails?.access_ends_at || recordingDetails?.endDate

    useEffect( () => {
        if ( !accessEndsAt || !classDay ) return

        const endTime = dayjs( accessEndsAt )
        if ( !endTime.isValid() ) return

        const tick = () => {
            setRemainingTimeInSeconds( endTime.diff( dayjs(), "second", true ) )
            // Sticky: once the student has started watching, keep tracking even if they pause
            // (e.g. to type an extension reason) so the overlay doesn't flicker away.
            if ( isVideoPlaying( containerEl ) )
                setHasStartedPlaying( true )
        }

        tick()
        const intervalId = setInterval( tick, TICK_INTERVAL_MS )
        return () => clearInterval( intervalId )
    }, [accessEndsAt, containerEl, classDay] )

    if ( !classDay )
        return null

    if ( !accessEndsAt || videoCompleted )
        return null

    if ( extensionRequest )
        return null

    const phase = remainingTimeInSeconds <= 0
        ? PHASE.EXPIRED
        : remainingTimeInSeconds <= warningThresholdInSeconds ? PHASE.CLOSING : null

    // Dismissing the "closing" warning still lets the "expired" notice show once time runs out
    if ( !phase || !hasStartedPlaying || dismissedPhase === phase )
        return null

    const expired = phase === PHASE.EXPIRED
    const accentColor = expired ? palette.error.main : palette.warning.main
    const trimmedReason = reason.trim()
    const reasonTooLong = trimmedReason.length > MAX_EXTENSION_REASON_LENGTH

    const submitRequest = async () => {
        if ( !trimmedReason || reasonTooLong || !onRequestExtension ) return
        const success = await onRequestExtension( trimmedReason )
        if ( success ) setReason( "" )
    }

    const dismiss = () => setDismissedPhase( phase )

    return (
        <Box
            role="alert"
            aria-live="assertive"
            // Stop keystrokes from reaching the video player's keyboard shortcuts (space = pause etc.)
            onKeyDown={e => e.stopPropagation()}
            sx={{
                position: "absolute",
                zIndex: 11,
                top: 72,
                right: { xs: 10, sm: 20 },
                width: { xs: "calc(100% - 20px)", sm: 360 },
                bgcolor: palette.background.paper,
                border: `1px solid ${accentColor}`,
                borderLeft: `4px solid ${accentColor}`,
                borderRadius: 2,
                boxShadow: 6,
                padding: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography component="span" display="flex" fontSize="20px" sx={{ color: accentColor }}>{Icons.default.Clock}</Typography>
                    <Typography fontWeight="700" fontSize="15px">
                        {expired ? "Class access has ended" : "Class access is closing"}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                    {!expired && <Typography fontWeight="700" fontSize="14px" sx={{ color: accentColor, fontVariantNumeric: "tabular-nums" }}>
                        {formatRemainingTime( remainingTimeInSeconds )}
                    </Typography>}
                    <IconButton size="small" aria-label="Dismiss" onClick={dismiss}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
            </Box>

            <Typography fontSize="13px" color="textSecondary">
                {expired
                    ? "You can keep watching, but your progress is no longer being recorded and won't count towards attendance. Request extra time to avoid being marked absent."
                    : `Your access ends at ${dayjs( accessEndsAt ).format( "hh:mm A" )}. Anything watched after that won't count towards attendance. If you can't finish in time, request extra time.`}
            </Typography>

            {extensionRequest && <Typography fontSize="13px" fontWeight="600" color={extensionRequest.status === "accepted" ? "success" : "secondary"}>
                {extensionRequest.status === "accepted"
                    ? "Your extension request was approved. Close and reopen the video to continue with the new time."
                    : `Extension request sent${extensionRequest.requested_at ? ` at ${dayjs( extensionRequest.requested_at ).format( "hh:mm A" )}` : ""}. Waiting for faculty approval.`}
            </Typography>}

            {!extensionRequest && canRequestExtension && <Box>
                <TextField
                    value={reason}
                    onChange={e => setReason( e.target.value )}
                    disabled={requestingExtension}
                    placeholder="Reason for extension"
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    error={reasonTooLong}
                />
                <FormHelperText error={reasonTooLong}>
                    {trimmedReason.length} / {MAX_EXTENSION_REASON_LENGTH} characters
                </FormHelperText>
            </Box>}

            <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                <Button size="small" color="greyed" onClick={dismiss} disabled={requestingExtension}>Dismiss</Button>
                {!extensionRequest && canRequestExtension && <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={submitRequest}
                    disabled={requestingExtension || !trimmedReason || reasonTooLong}
                    endIcon={requestingExtension ? <CircularProgress size={14} /> : Icons.default.Clock}
                >
                    Request extension
                </Button>}
            </Box>
        </Box>
    )
}

export default AccessEndTimeTracker